<template>
  <property-report-card
    :printing-mode="printingMode"
    title="Rent Comps Overview"
    :description="
      printingMode
        ? 'See key comp data in one place.'
        : 'See key comp data in one place. Use filters to customize your view.'
    "
  >
    <template #actions>
      <q-btn flat no-caps @click="exportCSV">
        <q-icon name="download" class="q-mr-sm" size="medium" />Download
      </q-btn>
      <div v-if="displayResetButton">
        <q-btn no-caps flat dense @click="resetVisibility">Reset</q-btn>
      </div>
      <select-options
        v-model="buildingSelectedRows"
        :options="buildingRowOptions"
        label="Building Rows"
        auto-submit
        @disable-option="
          (o: Option) => $tracker.changedGridRowSelection('removed', 'building', o.value)
        "
        @enable-option="
          (o: Option) => $tracker.changedGridRowSelection('added', 'building', o.value)
        "
        @update:model-value="saveSelections"
      />
      <select-options
        v-model="unitSelectedRows"
        :options="unitRowOptions"
        label="Unit Rows"
        auto-submit
        @disable-option="
          (o: Option) => $tracker.changedGridRowSelection('removed', 'unit', o.value)
        "
        @enable-option="(o: Option) => $tracker.changedGridRowSelection('added', 'unit', o.value)"
        @update:model-value="saveSelections"
      />
    </template>
    <q-markup-table
      id="intercom-overview-rent-comps-content"
      flat
      bordered
      dense
      separator="cell"
      :style="printingMode ? '' : 'max-height: 80vh'"
    >
      <tbody>
        <tr
          v-for="row in visibleRows"
          :id="`intercom-overview-rent-comps-content-row-${row.name}`"
          :key="row.name"
          ref="allRows"
          class="avoid-break"
          :class="row.sticky && !printingMode ? 'sticky-row' : ''"
        >
          <td
            class="text-left label no-padding"
            :class="{ 'cursor-pointer': row.sortable !== false, 'sticky-column': pinLabels }"
            :style="row.label.style"
            @click="row.sortable !== false ? labelClicked(row.name) : undefined"
          >
            <div class="q-mx-sm">
              <span v-if="!row.label.style" class="q-mr-xs">
                {{ row.label.value }}
                <q-tooltip v-if="row.label.tooltip" max-width="250px">
                  {{ row.label.tooltip }}
                </q-tooltip>
              </span>
              <template v-if="row.sortable !== false">
                <template v-if="sortBy && sortBy.name === row.name">
                  <q-icon v-if="sortBy.direction === 'asc'" name="north" />
                  <q-icon v-if="sortBy.direction === 'desc'" name="south" />
                </template>
                <q-icon v-else id="sort-icon-swap-vert" name="swap_vert" />
              </template>
              <span v-if="row.label.style" class="q-ml-xs">
                {{ row.label.value }}
                <q-icon name="warning" v-if="row.label.warning" color="orange" />
                <q-tooltip v-if="row.label.tooltip" max-width="250px">
                  {{ row.label.tooltip }}
                </q-tooltip>
              </span>
              <q-icon
                v-if="!printingMode && row.name === 'title'"
                name="push_pin"
                class="cursor-pointer absolute push-pin-icon"
                :class="pinLabels ? 'inline-block' : 'text-grey-7'"
                style="right: 4px; top: 8px; transform: rotate(25deg)"
                @click="pinLabelsColumn"
              />
            </div>
          </td>
          <td
            v-for="prop in sortedFilteredProperties"
            :key="prop.prop.id"
            class="text-center no-padding"
            :class="getColumnClasses(prop)"
            :style="getColumnStyle(row)"
          >
            <div
              v-for="(field, fieldIdx) in [
                prop.prop.id === 'avg_column' ? computeAverageField(row) : row.field(prop)
              ]"
              :key="fieldIdx"
            >
              <template v-if="row.name.includes('street_view') && prop.prop.id === 'avg_column'">
                <div class="column justify-center items-center">
                  <q-icon name="equalizer" class="text-green-5" size="lg" />
                </div>
              </template>
              <template v-else-if="row.name.includes('street_view')">
                <img
                  v-if="!streetviewErrors[prop.prop.id]"
                  data-private
                  :src="field.value as string"
                  :alt="'Google Street View for ' + prop.prop.street_address"
                  width="125px"
                  height="82px"
                  @error="streetviewErrors[prop.prop.id] = true"
                />
                <div
                  v-else
                  class="bg-grey-3 column justify-center items-center"
                  :class="{ 'bg-grey-9': $q.dark.isActive }"
                >
                  <div style="width: 125px; height: 82px; line-height: 82px; text-align: center">
                    <q-icon name="apartment" class="text-grey-5" size="lg" />
                  </div>
                </div>
                <div class="absolute q-gutter-xs" style="z-index: 1; right: 5px; bottom: 10px">
                  <q-badge v-if="prop.prop.is_affordable" color="orange">
                    Affordable
                    <q-tooltip>
                      Detected Affordable Housing based on the LIHTC database and floorplan names.
                    </q-tooltip>
                  </q-badge>
                  <q-badge v-if="prop.prop.is_student" color="orange">
                    Student
                    <q-tooltip
                      >Detected Student Housing based on text and price analysis on the
                      property</q-tooltip
                    >
                  </q-badge>
                  <q-badge v-if="prop.prop.is_senior" color="orange">
                    Senior
                    <q-tooltip
                      >Detected Senior Housing based on text and data analysis on the property</q-tooltip
                    >
                  </q-badge>
                  <q-badge v-if="prop.prop.is_build_to_rent" color="orange">
                    Build to Rent
                    <q-tooltip
                      >Detected Build to Rent Housing based on text and data analysis on the property</q-tooltip
                    >
                  </q-badge>
                  <q-badge v-if="prop.prop.is_lease_up" color="orange"> Lease Up </q-badge>
                </div>
              </template>
              <div
                v-else-if="row.name === 'building_website' && field.value"
                class="q-mx-xs"
                style="overflow: hidden; white-space: nowrap"
              >
                <a
                  :href="getFullUrl(field.value)"
                  class="text-primary clickable-table-cell"
                  target="_blank"
                >
                  {{ row.format ? row.format(field.value) : field.value }}
                </a>
              </div>
              <!-- Similarity -->
              <template v-else-if="row.name === 'similarity'">
                <span
                  v-if="field.value"
                  class="cursor-pointer"
                  :class="{ 'clickable-table-cell': !printingMode }"
                >
                  {{ row.format ? row.format(field.value) : field.value }}
                  <span v-if="field.value !== null">
                    {{ field.unit }}
                  </span>
                  <q-menu
                    transition-duration="100"
                    @show="$tracker.openedPopup('similarity_breakdown', 'the_grid')"
                  >
                    <similarity-card
                      :similarities="similarities[prop.prop.id]"
                      :hidden-dimensions="prop.prop.is_student ? [] : ['distances_to_universities']"
                    />
                  </q-menu>
                </span>
                <span v-else>
                  {{ row.format ? row.format(field.value) : field.value }}
                  <span v-if="field.value !== null">
                    {{ field.unit }}
                  </span>
                </span>
              </template>
              <template v-else-if="row.name === 'quality'">
                <span class="cursor-pointer" :class="{ 'clickable-table-cell': !printingMode }">
                  {{ row.format ? row.format(field.value) : field.value }}
                  <q-menu
                    transition-duration="100"
                    max-width="250px"
                    @show="$tracker.openedPopup('quality_breakdown', 'the_grid')"
                  >
                    <div class="column q-gutter-xs items-center q-pa-sm">
                      <span class="text-center text-bold">Detected Quality</span>
                      <span
                        class="text-caption"
                        :class="$q.dark.isActive ? 'text-grey-5' : 'text-grey-7'"
                      >
                        HelloData’s rating on quality and condition based on the property photos.
                      </span>
                    </div>
                    <q-separator />
                    <q-markup-table flat dense square>
                      <thead>
                        <tr class="text-left">
                          <th scope="col" class="text-left">Room</th>
                          <th scope="col" class="text-right">Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="x in Object.keys(prop.prop.building_quality).filter(
                            (_) => prop.prop.building_quality[_]
                          )"
                          :key="x"
                          :class="{
                            'text-bold bg-grey-1': x === 'property_overall_quality',
                            'bg-grey-9': $q.dark.isActive && x === 'property_overall_quality'
                          }"
                        >
                          <td>
                            {{
                              humanizeString(
                                x
                                  .replace('avg_quality_score', '')
                                  .replace('property_', '')
                                  .replace('quality', '')
                              )
                            }}
                          </td>
                          <td class="text-right">
                            {{ Math.round(prop.prop.building_quality[x] * 10) }}/10
                          </td>
                        </tr>
                      </tbody>
                    </q-markup-table>
                  </q-menu>
                </span>
              </template>
              <template v-else-if="row.name === 'reviews'">
                <span class="cursor-pointer" :class="{ 'clickable-table-cell': !printingMode }">
                  {{ row.format ? row.format(field.value) : field.value }}
                  <q-menu
                    transition-duration="100"
                    max-width="500px"
                    @show="$tracker.openedPopup('review_breakdown', 'the_grid')"
                  >
                    <card-review-analysis
                      v-if="prop.prop.review_analysis"
                      :review-analysis="prop.prop.review_analysis"
                    />
                  </q-menu>
                </span>
              </template>
              <template v-else-if="row.name === 'pricing_strategy'">
                <span class="cursor-pointer" :class="{ 'clickable-table-cell': !printingMode }">
                  <div v-for="v in row.format(field.value)" :key="v">{{ v }}</div>
                  <q-menu
                    transition-duration="100"
                    @show="$tracker.openedPopup('pricing_strategy', 'the_grid')"
                  >
                    <card-pricing-strategy :pricing-strategy="field.value" />
                  </q-menu>
                </span>
              </template>
              <template v-else-if="row.name === 'concessions'">
                <show-concessions-popup-table-cell :property="prop.prop" :date-range="dateRange">
                  {{ field.value }}
                </show-concessions-popup-table-cell>
              </template>
              <template
                v-else-if="
                  (row.label.value === 'Effective Rent Trend' ||
                    row.label.value === 'Asking Rent Trend') &&
                  prop.prop.id !== 'avg_column'
                "
              >
                <div v-if="field.value.length" class="q-px-md row justify-center">
                  <chart-sparkline :values="field.value" :label="row.label.value" />
                </div>
              </template>
              <div v-else>
                <!-- Average column special handling -->
                <template v-if="prop.prop.id === 'avg_column'">
                  <template
                    v-if="row.name === 'title' || (printingMode && row.name.endsWith('ut_title'))"
                  >
                    <span class="text-weight-bold">Comp Avg</span>
                    <q-icon
                      v-if="!printingMode"
                      name="push_pin"
                      class="cursor-pointer absolute push-pin-icon"
                      :class="pinAverage ? 'inline-block' : 'text-grey-6'"
                      style="right: 4px; top: 8px; transform: rotate(25deg)"
                      @click="pinAverageColumn"
                    />
                  </template>
                  <template v-else-if="row.name === 'building_name'">
                    <span class="text-weight-bold">
                      {{
                        subjectIsVisible ? filteredProperties.length - 1 : filteredProperties.length
                      }}
                      Properties
                    </span>
                  </template>
                  <template v-else-if="row.name.includes('ut_unit_title')">
                    <q-icon v-if="!printingMode" name="help_outline" class="text-blue-5" size="xs">
                      <q-tooltip max-width="250px">
                        Calculated by averaging the averages to prevent large properties from
                        skewing results.
                      </q-tooltip>
                    </q-icon>
                  </template>
                  <template
                    v-else-if="
                      field.value === null ||
                      field.value === undefined ||
                      field.value === '' ||
                      Array.isArray(field.value)
                    "
                  >
                  </template>
                  <div v-else>
                    <span>{{ row.format ? row.format(field.value) : field.value }}</span>
                    <span
                      v-if="showUnitLabel(field)"
                      class="text-grey-7"
                      style="padding-left: 2px"
                      >{{ field.unit }}</span
                    >
                  </div>
                </template>
                <!-- Regular property columns -->
                <template v-else>
                  <span v-if="row.notApplicable"> N/A </span>
                  <span
                    v-else
                    :class="{
                      'cursor-pointer': field.units.length,
                      'clickable-table-cell': !printingMode && field.units.length
                    }"
                    @click="
                      field.units.length
                        ? openUnitPopup(row.label.value, prop.prop, field, row)
                        : undefined
                    "
                  >
                    {{ row.format ? row.format(field.value) : field.value }}
                  </span>
                  <span
                    v-if="showUnitLabel(field) && !row.notApplicable"
                    class="text-grey-7"
                    style="font-size: 11px; padding-left: 2px"
                  >
                    {{ field.unit }}
                  </span>
                  <q-icon
                    v-if="
                      !printingMode && row.name === 'title' && prop.prop.id === subjectDetail?.id
                    "
                    name="push_pin"
                    class="absolute cursor-pointer push-pin-icon"
                    :class="pinSubject ? 'inline-block' : 'text-grey-6'"
                    style="right: 4px; top: 8px; transform: rotate(25deg)"
                    @click="pinSubjectColumn"
                  />
                </template>
              </div>
            </div>
          </td>
          <template v-if="numColumns < 9">
            <td v-for="x in 10 - numColumns" :key="x" style="width: 130px">
              <q-btn
                v-if="row.name === 'title' && x === 1 && showAddCompsBtn"
                no-caps
                size="sm"
                dense
                unelevated
                flat
                color="primary"
                class="q-px-sm sticky-row text-weight-bold"
                icon="add"
                :to="{
                  name: 'Edit Property Report',
                  params: { reportId: $route.params.reportId },
                  hash: '#add-rent-comps'
                }"
                @click="$tracker.addCompsButtonClicked()"
              >
                Add Comps
              </q-btn>
            </td>
          </template>
        </tr>
      </tbody>
    </q-markup-table>
    <q-dialog v-model="displayUnitPopup" transition-duration="100" @before-hide="closeUnitPopup">
      <q-card v-if="clickedCell" style="max-width: 1600px">
        <q-card-section>
          <div class="row justify-between">
            <div>
              <div>
                {{ clickedCell.prop.building_name || clickedCell.prop.street_address }}
              </div>
              <div class="row q-gutter-x-sm items-center">
                <div class="text-h5 text-bold">
                  {{ clickedCell.label }} -
                  {{
                    clickedCell.row.format
                      ? clickedCell.row.format(clickedCell.field.value)
                      : clickedCell.field.value
                  }}
                  {{ clickedCell.field.unit || '' }}
                </div>
              </div>
            </div>
            <q-icon size="sm" name="close" class="cursor-pointer" @click="closeUnitPopup" />
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <property-compare-unit-details
            :expanded-unit-history="expandedUnitHistory"
            :units-with-properties="clickedCell.unitsWithProp"
            :date-range="dateRange"
            hide-filter
          />
        </q-card-section>
      </q-card>
    </q-dialog>
    <q-dialog
      v-model="displayConcessionsPopup"
      transition-duration="100"
      @before-hide="closeConcessionsPopup"
    >
      <q-card v-if="clickedConcessionsProperty" style="max-width: 1600px">
        <q-card-section>
          <div class="row justify-between">
            <div>
              <div>
                {{
                  clickedConcessionsProperty.building_name ||
                  clickedConcessionsProperty.street_address
                }}
              </div>
              <div class="row q-gutter-x-sm items-center">
                <div class="text-h5 text-bold">Concessions</div>
              </div>
            </div>
            <q-icon size="sm" name="close" class="cursor-pointer" @click="closeConcessionsPopup" />
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <property-compare-concessions
            :filtered-properties="
              filteredProperties.filter((_) => _.prop.id === clickedConcessionsProperty?.id)
            "
            default-status="all"
            :date-range="dateRange"
          />
        </q-card-section>
      </q-card>
    </q-dialog>
  </property-report-card>
</template>

<script lang="ts">
import { type PropType } from 'vue'
import {
  type Availability,
  type PropertyDetailsResponse,
  type Price,
  type AvailabilityPeriod,
  type Occupancy,
  type Similarities,
  getStreetViewUrl,
  haversineDist,
  humanizeQualityScore,
  humanizeReviewScore,
  humanizeString,
  min,
  max,
  avg,
  occupancyOverTime,
  avgPsf,
  minPsf,
  maxPsf,
  GridPreferences,
  daysVacant,
  daysOnMarket,
  numApplications,
  numDenials,
  getTrend,
  isVacant,
  sanitizeFieldForCSV,
  estimateUnitMix,
  type UnitMixRow,
  type Option
} from 'common'
import {
  formatMoney,
  formatPercent,
  formatNumber,
  unitTypeColor,
  getActiveConcessions,
  getFullUrl
} from '@/utils'
import { isLoaded } from '@/utils/loading'

import {
  parseISO,
  min as minDate,
  format,
  subDays,
  addDays,
  eachWeekOfInterval,
  endOfWeek,
  differenceInDays
} from 'date-fns'
import { enUS } from 'date-fns/locale/en-US'
import PropertyCompareUnitDetails from './PropertyCompareUnitDetails.vue'
import PropertyCompareConcessions from './PropertyCompareConcessions.vue'
import CardReviewAnalysis from '@/components/CardReviewAnalysis.vue'
import SimilarityCard from '@/components/SimilarityCard.vue'
import CardPricingStrategy from '@/components/CardPricingStrategy.vue'
import SelectOptions from '@/components/SelectOptions.vue'
import { usePropertyReportStore } from '@/stores/propertyreport'
import ChartSparkline from '@/components/Charts/ChartSparkline.vue'
import { exportFile } from 'quasar'
import ShowConcessionsPopupTableCell from '@/components/PropertyReports/ShowConcessionsPopupTableCell.vue'
import PropertyReportCard from '@/components/PropertyReportCard.vue'
import { useUserStore } from '@/stores/user.ts'

interface FilteredUnit {
  unit: Availability
  lastHistory: Price
  period: AvailabilityPeriod
}

interface FilteredProp {
  prop: PropertyDetailsResponse
  units: FilteredUnit[]
}

interface FieldResult {
  value: number | string | null
  units: FilteredUnit[]
  unit?: string
  showUnitOnZeroValue?: boolean
}

interface GridRow {
  label: { value: string; tooltip?: string; style?: string; align: string }
  name: string
  field: (column: FilteredProp) => FieldResult
  format?: (value: number | string | null) => string
  notApplicable?: boolean
  sortable?: boolean
  loading?: boolean
  style?: string
  sticky?: boolean
  hide?: boolean
}

const DEFAULT_UNIT_SELECT: Option[] = [
  { label: 'Available Units', value: 'available_units' },
  { label: 'Rent', value: 'avg_rent' },
  { label: 'Average Sqft', value: 'avg_sf' },
  { label: 'NER', value: 'avg_effective_rent' },
  { label: 'Rent/ft²', value: 'avg_rent_psf' },
  { label: 'NER/ft²', value: 'avg_effective_rent_psf' },
  { label: 'Effective Rent Trend', value: 'effective_rent_trend' },
  { label: 'Concession %', value: 'concession_percent' }
]

export default {
  name: 'PropertyCompareOverview',
  components: {
    PropertyReportCard,
    ShowConcessionsPopupTableCell,
    ChartSparkline,
    PropertyCompareUnitDetails,
    PropertyCompareConcessions,
    CardPricingStrategy,
    CardReviewAnalysis,
    SimilarityCard,
    SelectOptions
  },
  props: {
    subjectDetail: {
      type: [Object, null] as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    details: {
      type: Array<PropertyDetailsResponse>,
      required: true
    },
    filteredProperties: {
      type: Array<FilteredProp>,
      required: true
    },
    expandedUnitHistory: {
      type: Object as PropType<{
        [unit_id: string]: {
          created_on: string
          history: Price
        }[]
      }>,
      required: true
    },
    similarities: {
      type: Object as PropType<{ [key: string]: Similarities }>,
      default: null
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    },
    legacyPrintingMode: {
      type: Boolean,
      default: false
    },
    startCompNumberingAt: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      pinSubject: true,
      pinLabels: true,
      pinAverage: true,
      streetviewErrors: {} as { [key: string]: boolean },
      displayUnitPopup: false,
      buildingSelectedRows: [] as Option[],
      unitSelectedRows: DEFAULT_UNIT_SELECT,
      clickedCell: null as null | {
        label: string
        prop: PropertyDetailsResponse
        field: FieldResult
        row: GridRow
        unitsWithProp: Array<
          Availability & {
            prop: PropertyDetailsResponse
          }
        >
      },
      clickedConcessionsProperty: null as PropertyDetailsResponse | null,
      displayConcessionsPopup: false,
      sortBy: null as { name: string; direction: 'desc' | 'asc' } | null
    }
  },
  computed: {
    defaultBuildingSelect(): Option[] {
      const baseOptions = [
        { label: 'Management Company', value: 'management_company' },
        { label: 'Property Website', value: 'building_website' },
        { label: 'Distance (mi)', value: 'distance' }
      ]

      if (this.subjectDetail) {
        baseOptions.push({ label: 'Similarity', value: 'similarity' })
      }

      baseOptions.push(
        { label: 'Year Built', value: 'year_built' },
        { label: '# Units', value: 'number_units' },
        { label: 'Leased % (Advertised)', value: 'leased_percent' },
        { label: 'Exposure', value: 'exposure' },
        { label: 'Quality', value: 'quality' },
        { label: 'Reviews', value: 'reviews' },
        { label: 'Pricing Strategy', value: 'pricing_strategy' },
        { label: 'Available Units', value: 'available_units' },
        { label: 'Rent', value: 'avg_rent' },
        { label: 'NER', value: 'avg_effective_rent' },
        { label: 'Leases (Last 30-Days)', value: 'leases_30_days' },
        { label: 'Leases (Last 7-Days)', value: 'leases_7_days' },
        { label: 'Leases / Week (Last 4 Weeks)', value: 'leases_per_week' },
        { label: 'Concessions', value: 'concessions' }
      )

      return baseOptions
    },
    sortedFilteredProperties() {
      let properties = this.filteredProperties

      if (this.sortBy) {
        const { direction, name } = this.sortBy
        const row = this.allRows.find((_) => _.name === name)!
        properties = this.filteredProperties.toSorted((a, b) => {
          const aValue = row.field(a).value
          const bValue = row.field(b).value
          // Handle null values first
          if (aValue === null && bValue === null) return 0
          if (aValue === null) return direction === 'asc' ? 1 : -1
          if (bValue === null) return direction === 'asc' ? -1 : 1
          // Handle numbers
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            return direction === 'asc' ? aValue - bValue : bValue - aValue
          }
          // Handle strings
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
          }
          // If the types are mixed or unsupported, keep the original order
          return 0
        })
      }

      // Insert average "property" right after subject (or at the beginning if no subject)
      if (properties.length > 1 && this.details.length > 1) {
        const averageProperty = {
          prop: {
            id: 'avg_column',
            building_name: 'Comp Avg',
            street_address: 'Average'
          } as PropertyDetailsResponse,
          units: [] as FilteredUnit[]
        }

        if (this.subjectDetail) {
          const subjectIndex = properties.findIndex((p) => p.prop.id === this.subjectDetail!.id)
          if (subjectIndex !== -1) {
            // Subject is present in filtered properties - insert after it
            properties = [
              ...properties.slice(0, subjectIndex + 1),
              averageProperty,
              ...properties.slice(subjectIndex + 1)
            ]
          } else {
            // Subject exists but is filtered out - insert at beginning
            properties = [averageProperty, ...properties]
          }
        } else {
          properties = [averageProperty, ...properties]
        }
      }

      return properties
    },
    buildingRowOptions() {
      const options: Option[] = this.allRows
        .filter((row) => !row.hide)
        .map((row) => ({ label: row.label.value, value: row.name }))
        .filter((option) => option.label && !option.value.includes('ut_'))
      return options
    },
    unitRowOptions() {
      const lookup: { [key: string]: Option } = {}
      for (const row of this.allRows) {
        if (row.name.includes('ut_')) {
          const value = row.name.slice(5)
          if (value !== 'title') {
            lookup[value] = { label: row.label.value, value }
          }
        }
      }
      return Object.values(lookup)
    },
    visibleRows() {
      return [
        { label: 'title', value: 'title' },
        { label: 'street_view', value: 'street_view' },
        { label: 'building_name', value: 'building_name' },
        { label: 'street_address', value: 'street_address' }
      ]
        .concat(this.buildingSelectedRows)
        .map((name) => this.allRows.find((_) => _.name === name.value)!)
        .concat(
          this.beds.flatMap((bed) => {
            // We include the header rows above each unit type for the PDF export
            const headerRows =
              this.printingMode && !this.legacyPrintingMode
                ? [
                    { label: 'title', value: `${bed}_ut_title` },
                    { label: 'street_view', value: `${bed}_ut_street_view` },
                    { label: 'building_name', value: `${bed}_ut_building_name` },
                    { label: 'street_address', value: `${bed}_ut_street_address` }
                  ]
                : []

            return headerRows
              .concat([{ label: 'unit_title', value: `${bed}_ut_unit_title` }])
              .concat(
                this.unitSelectedRows.map((row) => ({
                  label: row.label,
                  value: `${bed}_ut_${row.value}`
                }))
              )
              .map((name) => this.allRows.find((_) => _.name === name.value)!)
          })
        )
    },
    allRows() {
      return [
        {
          label: { value: '', align: '' },
          name: 'title',
          sortable: false,
          sticky: true,
          style: 'font-weight: bold;',
          field: this.computeTitle
        },
        {
          label: { value: '', align: '' },
          name: 'street_view',
          sortable: false,
          field: this.computeStreetView
        },
        {
          label: { value: '', align: '' },
          name: 'building_name',
          sortable: false,
          style: 'font-weight: bold; height: 40px;',
          sticky: true,
          field: this.computeBuildingName
        },
        {
          label: { value: '', align: '' },
          name: 'street_address',
          sticky: true,
          sortable: false,
          field: this.computeStreetAddress
        },
        {
          label: {
            value: 'Management Company',
            align: '',
            tooltip: 'The company responsible for managing the property.'
          },
          name: 'management_company',
          style: 'font-size: 11px;',
          sortable: false,
          field: this.computeManagementCompany
        },
        {
          label: {
            value: 'Property Website',
            align: '',
            tooltip: "Visit the property's official website for more details."
          },
          name: 'building_website',
          sortable: false,
          field: this.computeBuildingWebsite,
          format: (value) => {
            if (!value) return ''
            try {
              return new URL(getFullUrl(value)).hostname.replace('www.', '')
            } catch (error) {
              return value
            }
          }
        },
        {
          label: {
            value: 'Distance (mi)',
            align: 'left',
            tooltip:
              'The distance in miles between this property and the selected subject property.'
          },
          name: 'distance',
          field: this.computeDistance,
          format: (value) => formatNumber(value, undefined, 2)
        },
        {
          label: {
            value: 'Similarity',
            align: 'left',
            tooltip:
              "A percentage from 0 to 100, where 100 means it's the most similar to your property. The score is based on several factors, which you can see by clicking the score. We adjust the importance of each factor depending on the market."
          },
          name: 'similarity',
          field: this.computeSimilarity,
          format: (value) => {
            if (value === 'Loading...') {
              return value
            }
            return value !== null ? formatNumber(value * 100, undefined, 1) : ''
          },
          hide: !this.subjectDetail
        },
        {
          label: {
            value: 'Year Built',
            align: 'left',
            tooltip:
              'The year the building was built. If no data is available, we show a prediction based on facade images.'
          },
          name: 'year_built',
          field: this.computeYearBuilt,
          format: (value) => (value ? Math.round(value).toString() : '')
        },
        {
          label: {
            value: '# Units',
            align: 'left',
            tooltip:
              'The total number of units in the building. If no data is available, we show a prediction based on property characteristics, location and units that have been listed.'
          },
          name: 'number_units',
          field: this.computeNumberUnits,
          format: (value) => formatNumber(value)
        },
        {
          label: {
            value: 'Stories',
            align: 'left',
            tooltip:
              'The number of stories in the building. We show a prediction based on facade images if no data is available.'
          },
          name: 'number_stories',
          field: this.computeNumberStories,
          format: (value) => (value ? Math.round(value).toString() : '')
        },
        {
          label: {
            value: 'Leased % (Advertised)',
            align: 'left',
            tooltip:
              'Estimated by deducting the # of units marked as available within the next 7 days, from the total # of units in the building / total units in the building.'
          },
          name: 'leased_percent',
          field: this.computeLeasedPercent,
          format: (value) => (value !== null ? formatNumber(value * 100, undefined, 1) : '')
        },
        {
          label: {
            value: 'Exposure',
            align: 'left',
            tooltip:
              'Exposure represents the percentage of units in a building currently listed as available for move-in within the next 6 months. This includes units that are either already vacant or not being renewed, divided by the total number of units in the building.'
          },
          name: 'exposure',
          field: this.computeExposure,
          format: (value) => (value !== null ? formatNumber(value * 100, undefined, 1) : '')
        },
        {
          label: {
            value: 'Quality',
            align: 'left',
            tooltip:
              'Our computer vision algorithms analyze interior property photos and assign individual room scores, which are combined into an overall quality rating for the property.'
          },
          name: 'quality',
          field: this.computeQuality,
          format: (value) => (value ? humanizeQualityScore(value) : '')
        },
        {
          label: {
            value: 'Reviews',
            align: 'left',
            tooltip:
              'We use natural language processing to evaluate tenant reviews, extracting sentiment on key factors.'
          },
          name: 'reviews',
          field: this.computeReviews,
          format: (value) => (value ? humanizeReviewScore(value) : '')
        },
        {
          label: {
            value: 'Pricing Strategy',
            align: 'left',
            tooltip:
              'We analyze unit pricing patterns at each property to deliver valuable pricing insights.'
          },
          name: 'pricing_strategy',
          style: 'font-size: 11px;',
          field: this.computePricingStrategy,
          format: (value) => {
            if (!value) return ''
            const { is_using_rev_management, avg_price_change, avg_duration } = value
            const revMgmt = is_using_rev_management ? 'Yes' : 'No'
            const askToClose = formatPercent(avg_price_change)
            const updated = formatNumber(avg_duration)
            return [
              `Rev Mgmt: ${revMgmt}`,
              `Ask to Close: ${askToClose}`,
              `Updates: Every ${updated} day${updated === '1' ? '' : 's'}`
            ]
          }
        },
        ...this.computeUnitDataRows((units) => units),
        {
          label: {
            value: 'Concessions',
            align: 'left',
            tooltip: 'Current concessions offered by the property during the selected time period.'
          },
          name: 'concessions',
          style: 'font-size: 11px;',
          field: this.computeLatestActiveConcession
        },
        ...this.beds.flatMap((bed) => this.computeUnitTypeRows(bed))
      ] as GridRow[]
    },
    beds() {
      if (!this.filteredProperties) {
        // this should never happen but does. Log to prevent runtime errors
        // to help debug next time it happens
        console.error('Unexpected: No filtered properties')
        return []
      }
      const beds = new Set<number>(
        this.filteredProperties
          .flatMap(({ units }) => units.map(({ unit }) => unit.bed))
          .filter((_) => _ !== null) as number[]
      )
      return [...beds].sort()
    },
    occupancies(): { [key: string]: Occupancy[] } {
      return this.filteredProperties.reduce(
        (acc, { prop }) => {
          acc[prop.id] = occupancyOverTime(prop, this.dateRange.from, this.dateRange.to)
          return acc
        },
        {} as { [key: string]: Occupancy[] }
      )
    },
    displayResetButton() {
      const list1 = this.defaultBuildingSelect.concat(DEFAULT_UNIT_SELECT)
      const list2 = this.buildingSelectedRows.concat(this.unitSelectedRows)
      if (list1.length !== list2.length) {
        return true
      }
      for (let i = 0; i < list1.length; i++) {
        if (list1[i].value !== list2[i].value) {
          return true
        }
      }
      return false
    },
    allPropertiesEstimatedUnitMixes(): {
      [propId: string]: UnitMixRow[]
    } {
      const allProperties = this.subjectDetail
        ? [this.subjectDetail].concat(this.details)
        : this.details
      const allUnitMixes: { [key: string]: UnitMixRow[] } = {}
      allProperties.forEach((property) => {
        allUnitMixes[property.id] = estimateUnitMix(property, 'bed')
      })
      return allUnitMixes
    },
    numColumns() {
      return this.filteredProperties.length
    },
    canEditPropertyReports() {
      return useUserStore().canEditPropertyReports
    },
    showAddCompsBtn() {
      const columnsInReport = this.subjectDetail ? this.details.length + 2 : this.details.length // +2 for subject and average

      return (
        this.canEditPropertyReports &&
        columnsInReport < 10 &&
        !this.printingMode &&
        this.subjectDetail
      )
    },
    daysInDateRange() {
      return differenceInDays(this.dateRange.to, this.dateRange.from) + 1
    },
    subjectIsVisible() {
      if (!this.subjectDetail) {
        return null
      }
      return this.filteredProperties.some((p) => p.prop.id === this.subjectDetail?.id)
    },
    userHasReadOnlyAccess() {
      const report = usePropertyReportStore().activeReport
      if (!isLoaded(report)) {
        return false
      }
      const userId = useUserStore().user?.sub
      if (!userId) {
        return false
      }
      const userAccess = report.data.access_users?.find((user) => user.id === userId)
      return userAccess?.read_only === true
    }
  },
  created() {
    this.buildingSelectedRows = this.defaultBuildingSelect

    const report = usePropertyReportStore().activeReport
    if (!isLoaded(report)) {
      return
    }
    const preferences = report.data.view_preferences.find((_) => _.name === 'the_grid')

    if (preferences) {
      this.buildingSelectedRows = preferences.building_rows.flatMap((_) => {
        const row = this.allRows.find((row) => row.name === _)
        return row ? { label: row.label.value, value: row.name } : []
      })

      this.unitSelectedRows = preferences.unit_rows.flatMap((_) => {
        const row = this.allRows.find((row) => row.name === _)
        return row ? { label: row.label.value, value: row.name } : []
      })
    }
  },
  mounted() {
    this.setStickyRowOffsets()
  },
  methods: {
    getFullUrl,
    humanizeString,
    setStickyRowOffsets() {
      const rows = this.$refs.allRows as HTMLDivElement[] | undefined
      if (!rows) {
        return
      }

      let cumulativeHeight = 0
      for (let index = 0; index < rows.length; index++) {
        const row = rows[index]
        if (row.classList.contains('sticky-row')) {
          // skip the second row, it's the street view picture
          if (index === 1) {
            return
          }

          row.style.top = `${cumulativeHeight}px`
          const offsetHeight = this.showAddCompsBtn ? row.offsetHeight : row.offsetHeight - 0.5
          cumulativeHeight += offsetHeight
        }
      }
    },
    computeTitle(col: FilteredProp) {
      const colIdx = this.filteredProperties.findIndex((p) => p.prop.id === col.prop.id)
      return {
        value:
          col.prop.id === this.subjectDetail?.id
            ? 'Subject'
            : `Comp ${colIdx + (this.subjectIsVisible ? 0 : 1) + this.startCompNumberingAt}`,
        units: []
      }
    },
    computeStreetView(col: FilteredProp) {
      if (col.prop.street_view_url) {
        return { value: col.prop.street_view_url, units: [] }
      }
      if (this.streetviewErrors[col.prop.id]) {
        return {
          value: getStreetViewUrl(col.prop, import.meta.env.VITE_API_URL),
          units: []
        }
      }
      const { street_address, city, state, zip_code } = col.prop
      const address = [street_address, city, state, zip_code].join(', ')
      return {
        value: `https://storage.googleapis.com/hello-data-ai-app-frontend/streetviews/${address}.jpeg`,
        units: []
      }
    },
    computeBuildingName(col: FilteredProp) {
      return { value: col.prop.building_name || col.prop.street_address, units: [] }
    },
    computeStreetAddress(col: FilteredProp) {
      return { value: col.prop.street_address, units: [] }
    },
    computeManagementCompany(col: FilteredProp) {
      return { value: col.prop.management_company, units: [] }
    },
    computeBuildingWebsite(col: FilteredProp) {
      const value = col.prop.building_website
      let urlWithProtocol = null
      try {
        if (value) {
          // Add protocol if missing
          urlWithProtocol = getFullUrl(value)
        }
      } catch (error) {
        console.error(error)
        urlWithProtocol = value
      }
      return { value: urlWithProtocol, units: [] }
    },
    computeDistance(col: FilteredProp) {
      const value =
        !this.subjectDetail || col.prop.id === this.subjectDetail.id
          ? null
          : haversineDist(this.subjectDetail, col.prop)
      return {
        value,
        unit: 'mi',
        units: []
      }
    },
    computeYearBuilt(col: FilteredProp) {
      const value = col.prop.year_built || col.prop.year_built_prediction
      return {
        value,
        units: []
      }
    },
    computeNumberUnits(col: FilteredProp) {
      const value = col.prop.number_units || col.prop.number_units_prediction || null
      return { value, units: [] }
    },
    computeNumberStories(col: FilteredProp) {
      const value = col.prop.number_stories || col.prop.number_stories_prediction
      return {
        value,
        units: []
      }
    },
    computeQuality(col: FilteredProp) {
      const value = col.prop.building_quality.property_overall_quality
      return {
        value,
        units: []
      }
    },
    computeReviews(col: FilteredProp) {
      const value = col.prop.review_analysis?.avg_score || null
      return {
        value,
        units: []
      }
    },
    computePricingStrategy(col: FilteredProp) {
      return {
        value: col.prop.pricing_strategy,
        units: []
      }
    },
    computeSimilarity(col: FilteredProp) {
      if (this.similarities === null) {
        return { value: null, unit: '', units: [] }
      }
      if (col.prop.id === this.subjectDetail?.id) {
        return { value: null, unit: '', units: [] }
      }
      if (!this.similarities[col.prop.id]?.overall) {
        return { value: 'Loading...', unit: '', units: [] }
      }
      const value = this.similarities[col.prop.id]?.overall
      if (value) {
        return {
          value,
          unit: '%',
          units: []
        }
      }
      return { value: null, unit: '', units: [] }
    },
    computeLeasedPercent(col: FilteredProp) {
      const relevantOccupancy = this.occupancies[col.prop.id]
      let value = null
      if (relevantOccupancy[relevantOccupancy.length - 1]) {
        value = relevantOccupancy[relevantOccupancy.length - 1].advertisedOccupancy
      }
      return {
        value,
        unit: '%',
        units: []
      }
    },
    computeExposure(col: FilteredProp) {
      const relevantOccupancy = this.occupancies[col.prop.id]
      let value = null
      if (relevantOccupancy[relevantOccupancy.length - 1]) {
        value = relevantOccupancy[relevantOccupancy.length - 1].exposure
      }
      return {
        value,
        unit: '%',
        units: []
      }
    },
    computeLeasesOverNDays(col: FilteredProp, n: number) {
      const NDaysAgo = format(subDays(this.dateRange.to, n), 'yyyy-MM-dd')
      const maxDate = format(this.dateRange.to, 'yyyy-MM-dd')
      const units = col.units.filter(
        ({ period }) =>
          period.exit_market && period.exit_market >= NDaysAgo && period.exit_market <= maxDate
      )
      return {
        value: units.length > 0 ? units.length : null,
        unit: units.length === 1 ? ' lease' : ' leases',
        units
      }
    },
    computeAvgLeasesOverNWeeks(col: FilteredProp, n: number) {
      const endOfLastWeek = endOfWeek(subDays(this.dateRange.to, 7), { weekStartsOn: 1 })
      const beginningOfNWeeksAgo = subDays(endOfLastWeek, n * 7 - 1)
      const beginningOfNWeeksAgoStr = format(beginningOfNWeeksAgo, 'yyyy-MM-dd')
      const endOfLastWeekStr = format(endOfLastWeek, 'yyyy-MM-dd')
      const units = col.units.filter(
        ({ period }) =>
          period.exit_market &&
          period.exit_market >= beginningOfNWeeksAgoStr &&
          period.exit_market <= endOfLastWeekStr
      )
      const data = eachWeekOfInterval(
        {
          start: beginningOfNWeeksAgo,
          end: endOfLastWeek
        },
        {
          weekStartsOn: 1,
          locale: enUS
        }
      ).map((start) => {
        const weekStart = format(start, 'yyyy-MM-dd')
        const weekEnd = format(addDays(start, 6), 'yyyy-MM-dd')
        return units.filter(
          ({ period }) =>
            period.exit_market && period.exit_market >= weekStart && period.exit_market <= weekEnd
        ).length
      })
      const totalLeases = data.reduce((a, b) => a + b, 0)
      const value = data.length > 0 && totalLeases > 0 ? totalLeases / data.length : null
      return {
        value,
        unit: units.length === 1 ? ' lease' : ' leases',
        units
      }
    },
    computeLatestActiveConcession(col: FilteredProp) {
      const activeConcession = getActiveConcessions(col.prop)[0]
      const text = activeConcession?.concessions || ''
      return { value: text.length > 115 ? text.slice(0, 115) + '...' : text, units: [] }
    },
    computeRent(
      col: FilteredProp,
      aggFnc: (vals: (number | null)[]) => number | null,
      effective?: boolean
    ) {
      const prices: (number | null)[] = []
      const useMax = aggFnc === max
      for (const { lastHistory } of col.units) {
        if (effective) {
          prices.push(
            lastHistory.effective_price ||
              (useMax ? lastHistory.max_effective_price : lastHistory.min_effective_price)
          )
        } else {
          prices.push(lastHistory.price || (useMax ? lastHistory.max_price : lastHistory.min_price))
        }
      }
      const value = aggFnc(prices)
      return {
        value,
        unit: effective ? 'NER' : undefined,
        units: col.units
      }
    },
    computeEffRentByRentRoll(
      col: FilteredProp,
      aggFnc: (vals: (number | null)[]) => number | null,
      bedCount: number | undefined,
      psf?: boolean
    ) {
      let prices: (number | null)[] = []
      let pricesWithSqft: { price: number | null; sqft: number | null }[] = []
      const propUnitMix = this.allPropertiesEstimatedUnitMixes[col.prop.id]

      if (bedCount !== undefined) {
        const unitsByTypeFromMix = propUnitMix.find((mix) => mix.bed === bedCount)
        if (unitsByTypeFromMix) {
          prices = psf
            ? [unitsByTypeFromMix.avgEffectivePsf]
            : [unitsByTypeFromMix.avgEffectiveRent]
        } else {
          console.error('No units by type from mix, this should never happen')
        }
      } else {
        if (psf) {
          pricesWithSqft = propUnitMix.flatMap((mix) =>
            mix.units.map((_) => {
              return {
                price: _.effective_price || _.min_effective_price,
                sqft: _.sqft
              }
            })
          )
        } else {
          prices = propUnitMix.flatMap((mix) =>
            mix.units.map((_) => _.effective_price || _.min_effective_price)
          )
        }
      }
      const value = psf && bedCount === undefined ? avgPsf(pricesWithSqft) : aggFnc(prices)
      return {
        value: value,
        unit: psf ? '/ft²' : 'NER',
        units: col.units
      }
    },
    computeSqft(col: FilteredProp, aggFnc: (vals: (number | null)[]) => number | null) {
      const sqfts: (number | null)[] = []
      const useMax = aggFnc === max
      for (const { unit } of col.units) {
        sqfts.push(unit.sqft || (useMax ? unit.max_sqft : unit.min_sqft))
      }
      const value = aggFnc(sqfts)
      return { value, unit: 'ft²', units: col.units }
    },
    computeRentPSF(
      col: FilteredProp,
      aggFnc: (
        vals: {
          price: number | null
          sqft: number | null
        }[]
      ) => number | null,
      effective?: boolean
    ) {
      const psfs: { price: number | null; sqft: number | null }[] = []
      const useMax = aggFnc === maxPsf
      for (const { unit, lastHistory } of col.units) {
        const sqft = unit.sqft || (useMax ? unit.max_sqft : unit.min_sqft)
        const price = effective
          ? lastHistory.effective_price ||
            (useMax ? lastHistory.max_effective_price : lastHistory.min_effective_price)
          : lastHistory.price || (useMax ? lastHistory.max_price : lastHistory.min_price)
        psfs.push({ price, sqft })
      }
      const value = aggFnc(psfs)
      return { value, unit: '/ft²', units: col.units }
    },
    computeDaysVacant(col: FilteredProp) {
      if (col.units.length === 0) {
        return { value: null, unit: 'days', units: col.units }
      }
      const allDaysVacant = col.units.reduce((acc, curr) => {
        const value = daysVacant(curr.period, this.dateRange.to)
        if (value) {
          acc += value
        }
        return acc
      }, 0)
      const value = allDaysVacant / col.units.length
      return { value, unit: 'days', units: col.units }
    },
    computeDaysOnMkt(col: FilteredProp) {
      if (col.units.length === 0) {
        return { value: null, unit: 'days', units: col.units }
      }
      const allDaysOnMkt = col.units.reduce((acc, curr) => {
        const value = daysOnMarket(curr.period, this.dateRange.to)
        if (value) {
          acc += value
        }
        return acc
      }, 0)
      const value = allDaysOnMkt / col.units.length
      return { value, unit: 'days', units: col.units }
    },
    computeConcessionPercent(col: FilteredProp) {
      if (col.units.length === 0) {
        return { value: null, unit: '%', units: col.units }
      }
      const allConcessionPerc = col.units.reduce((acc, curr) => {
        const lastHistory = curr.lastHistory
        if (lastHistory.price && lastHistory.effective_price) {
          acc.push(((lastHistory.price - lastHistory.effective_price) / lastHistory.price) * 100)
        }
        return acc
      }, [] as number[])
      const value = avg(allConcessionPerc)
      return {
        value,
        unit: '%',
        units: col.units,
        showUnitOnZeroValue: true
      }
    },
    computeConcessionAmount(col: FilteredProp) {
      if (col.units.length === 0) {
        return { value: null, unit: '', units: col.units }
      }
      const totalConcessionAmounts = col.units.reduce((acc, curr) => {
        const { lastHistory } = curr
        if (lastHistory.price && lastHistory.effective_price) {
          acc.push(lastHistory.price - lastHistory.effective_price)
        }
        return acc
      }, [] as number[])
      const value = avg(totalConcessionAmounts)
      return {
        value,
        units: col.units
      }
    },
    computeTotalConcessionAmount(col: FilteredProp) {
      if (col.units.length === 0) {
        return { value: null, unit: '', units: col.units }
      }
      const totalConcessionAmounts = col.units.reduce((acc, curr) => {
        const { unit, lastHistory } = curr
        const leaseTermInMonths = unit.lease_term ?? 12
        if (lastHistory.price && lastHistory.effective_price) {
          const monthlyConcessionAmount = lastHistory.price - lastHistory.effective_price
          acc.push(monthlyConcessionAmount * leaseTermInMonths)
        }
        return acc
      }, [] as number[])
      const value = avg(totalConcessionAmounts)
      return {
        value,
        units: col.units
      }
    },
    computeCancelPercent(col: FilteredProp) {
      if (!col.units.length) {
        return { value: null, unit: '%', units: col.units }
      }

      const minDateStr = format(this.dateRange.from, 'yyyy-MM-dd')
      const maxDateStr = format(this.dateRange.to, 'yyyy-MM-dd')

      let numApps = 0
      let numCancels = 0

      col.units.forEach((unit) => {
        numApps += numApplications(unit.period, { from: minDateStr, to: maxDateStr })
        numCancels += numDenials(unit.period, { from: minDateStr, to: maxDateStr })
      })

      const value = numApps ? (numCancels / numApps) * 100 : null
      return {
        value,
        unit: '%',
        units: col.units,
        showUnitOnZeroValue: true
      }
    },
    computeNumCancels(col: FilteredProp) {
      const minDateStr = format(this.dateRange.from, 'yyyy-MM-dd')
      const maxDateStr = format(this.dateRange.to, 'yyyy-MM-dd')
      if (!col.units.length) {
        return { value: null, unit: '%', units: col.units }
      }

      let value = 0
      const units: FilteredUnit[] = []
      col.units.forEach((unit) => {
        const denials = numDenials(unit.period, { from: minDateStr, to: maxDateStr })
        if (denials > 0) {
          value += denials
          units.push(unit)
        }
      })

      return {
        value,
        unit: value > 1 ? 'cancels' : 'cancel',
        units
      }
    },
    computeNumApps(col: FilteredProp) {
      if (!col.units.length) {
        return { value: null, unit: '%', units: col.units }
      }

      let value = 0

      const minDateStr = format(this.dateRange.from, 'yyyy-MM-dd')
      const maxDateStr = format(this.dateRange.to, 'yyyy-MM-dd')

      const units: FilteredUnit[] = []
      col.units.forEach((unit) => {
        const apps = numApplications(unit.period, { from: minDateStr, to: maxDateStr })
        if (apps > 0) {
          value += apps
          units.push(unit)
        }
      })

      return {
        value,
        unit: value > 1 ? 'apps' : 'app',
        units
      }
    },
    computeNumVacancies(col: FilteredProp) {
      if (!col.units.length) {
        return { value: null, unit: 'units', units: col.units }
      }

      const leaseUp = col.prop.is_lease_up ?? false
      const units: FilteredUnit[] = []
      col.units.forEach((unit) => {
        if (isVacant(unit.period, leaseUp, this.dateRange.to.toISOString())) {
          units.push(unit)
        }
      })

      return {
        value: units.length,
        unit: units.length > 1 ? 'vacancies' : 'vacancy',
        units
      }
    },
    computeEffectiveRentTrend(col: FilteredProp) {
      const effectiveRentHistory = getTrend(
        col.units,
        this.expandedUnitHistory,
        format(this.dateRange.from, 'yyyy-MM-dd'),
        format(this.dateRange.to, 'yyyy-MM-dd'),
        'effective_rent'
      )

      return {
        value: effectiveRentHistory,
        units: col.units
      }
    },
    computeAskingRentTrend(col: FilteredProp) {
      const askingRentHistory = getTrend(
        col.units,
        this.expandedUnitHistory,
        format(this.dateRange.from, 'yyyy-MM-dd'),
        format(this.dateRange.to, 'yyyy-MM-dd'),
        'asking_rent'
      )

      return {
        value: askingRentHistory,
        units: col.units
      }
    },
    availableUnits(col: FilteredProp) {
      const { prop, units } = col
      const newMaxDate = this.dateRange.to
        ? minDate([parseISO(prop.created_on), this.dateRange.to])
        : parseISO(prop.created_on)
      const newMaxDateStr = format(newMaxDate, 'yyyy-MM-dd')
      const applicableUnits = units.filter(
        ({ lastHistory, period }) =>
          lastHistory.to_date >= newMaxDateStr && period.exit_market !== newMaxDateStr
      )
      return {
        value: applicableUnits.length > 0 ? applicableUnits.length : null,
        unit: applicableUnits.length === 1 ? 'unit' : 'units',
        units: applicableUnits
      }
    },
    computeUnitDataRows(
      unitFilterFnc: (units: FilteredProp['units']) => FilteredUnit[],
      bedCount?: number
    ) {
      return [
        {
          label: {
            value: 'Estimated Total Units',
            align: 'left',
            tooltip: 'The overall estimated total number of units in the property.'
          },
          name: 'estimated_total_units',
          field: ({ prop }) => {
            const unitMix = this.allPropertiesEstimatedUnitMixes[prop.id].find(
              (mix) => mix.bed === bedCount
            )
            if (!unitMix) {
              return {
                value: null,
                unit: 'units',
                units: []
              }
            }
            return {
              value: unitMix.estimatedUnits,
              unit: 'units',
              units: []
            }
          },
          format: (value) => formatNumber(value)
        },
        {
          label: {
            value: 'Available Units',
            align: 'left',
            tooltip:
              'The total number of units currently advertised as available for each property.'
          },
          name: 'available_units',
          field: ({ prop, units }) => this.availableUnits({ prop, units: unitFilterFnc(units) }),
          format: (value) => (value ? formatNumber(value) : '')
        },
        {
          label: {
            value: 'Minimum Rent',
            align: 'left',
            tooltip: 'The minimum asking rent of all the units in the selected time period.'
          },
          name: 'min_rent',
          field: ({ prop, units }) => this.computeRent({ prop, units: unitFilterFnc(units) }, min),
          format: (value) => formatMoney(value, 0)
        },
        {
          label: {
            value: 'Rent',
            align: 'left',
            tooltip: 'The average asking rent of all the units in the selected time period.'
          },
          name: 'avg_rent',
          field: ({ prop, units }) => this.computeRent({ prop, units: unitFilterFnc(units) }, avg),
          format: (value) => formatMoney(value, 0)
        },
        {
          label: {
            value: 'Maximum Rent',
            align: 'left',
            tooltip: 'The maximum asking rent of all the units in the selected time period.'
          },
          name: 'max_rent',
          field: ({ prop, units }) => this.computeRent({ prop, units: unitFilterFnc(units) }, max),
          format: (value) => formatMoney(value, 0)
        },
        {
          label: {
            value: 'Minimum Sqft',
            tooltip: 'The minimum square feet of all the units in the selected time period.'
          },
          name: 'min_sf',
          field: ({ prop, units }) => this.computeSqft({ prop, units: unitFilterFnc(units) }, min),
          format: (value) => formatNumber(value)
        },
        {
          label: {
            value: 'Average Sqft',
            tooltip: 'The average square feet of all the units in the selected time period.'
          },
          name: 'avg_sf',
          field: ({ prop, units }) => this.computeSqft({ prop, units: unitFilterFnc(units) }, avg),
          format: (value) => formatNumber(value)
        },
        {
          label: {
            value: 'Max Sqft',
            tooltip: 'The maximum square feet of all the units in the selected time period.'
          },
          name: 'max_sf',
          field: ({ prop, units }) => this.computeSqft({ prop, units: unitFilterFnc(units) }, max),
          format: (value) => formatNumber(value)
        },
        {
          label: {
            value: 'Minimum NER',
            align: 'left',
            tooltip:
              'The minimum effective rent of all the units in the selected time period. Our AI extracts the dollar amounts from concession banners and amortizes them over the lease term.'
          },
          name: 'min_effective_rent',
          field: ({ prop, units }) =>
            this.computeRent({ prop, units: unitFilterFnc(units) }, min, true),
          format: (value) => formatMoney(value, 0)
        },
        {
          label: {
            value: 'NER',
            align: 'left',
            tooltip:
              'The average effective rent of all the units in the selected time period. Our AI extracts the dollar amounts from concession banners and amortizes them over the lease term.'
          },
          name: 'avg_effective_rent',
          field: ({ prop, units }) =>
            this.computeRent({ prop, units: unitFilterFnc(units) }, avg, true),
          format: (value) => formatMoney(value, 0)
        },
        {
          label: {
            value: 'NER (Estimated Rent Roll)',
            align: 'left',
            tooltip:
              'Using the same logic as our Estimated Rent Roll, the average effective rent of all units regardless of the selected time period.'
          },
          name: 'avg_effective_rent_rent_roll',
          field: ({ prop, units }) =>
            this.computeEffRentByRentRoll({ prop, units: unitFilterFnc(units) }, avg, bedCount),
          format: (value) => formatMoney(value, 0)
        },
        {
          label: {
            value: 'NER/ft² (Estimated Rent Roll)',
            align: 'left',
            tooltip:
              'Using the same logic as our Estimated Rent Roll, the average effective rent per square feet of all units regardless of the selected time period.'
          },
          name: 'avg_effective_rent_psf_rent_roll',
          field: ({ prop, units }) =>
            this.computeEffRentByRentRoll(
              { prop, units: unitFilterFnc(units) },
              avg,
              bedCount,
              true
            ),
          format: (value) => formatMoney(value, 2)
        },
        {
          label: {
            value: 'Maximum NER',
            align: 'left',
            tooltip:
              'The maximum effective rent of all the units in the selected time period. Our AI extracts the dollar amounts from concession banners and amortizes them over the lease term.'
          },
          name: 'max_effective_rent',
          field: ({ prop, units }) =>
            this.computeRent({ prop, units: unitFilterFnc(units) }, max, true),
          format: (value) => formatMoney(value, 0)
        },
        {
          label: {
            value: 'Minimum NER/ft²',
            tooltip:
              'The minimum effective rent per square foot of all the units in the selected time period.'
          },
          name: 'min_effective_rent_psf',
          field: ({ prop, units }) =>
            this.computeRentPSF({ prop, units: unitFilterFnc(units) }, minPsf, true),
          format: (value) => formatMoney(value, 2)
        },
        {
          label: {
            value: 'NER/ft²',
            tooltip:
              'The average effective rent per square foot divides the sum of all effective rent by the sum of all square foot.'
          },
          name: 'avg_effective_rent_psf',
          field: ({ prop, units }) =>
            this.computeRentPSF({ prop, units: unitFilterFnc(units) }, avgPsf, true),
          format: (value) => formatMoney(value, 2)
        },
        {
          label: {
            value: 'Maximum NER/ft²',
            tooltip:
              'The maximum effective rent per square foot of all the units in the selected time period.'
          },
          name: 'max_effective_rent_psf',
          field: ({ prop, units }) =>
            this.computeRentPSF({ prop, units: unitFilterFnc(units) }, maxPsf, true),
          format: (value) => formatMoney(value, 2)
        },
        {
          label: {
            value: 'Minimum Rent/ft²',
            tooltip:
              'The minimum asking rent per square foot of all the units in the selected time period.'
          },
          name: 'min_rent_psf',
          field: ({ prop, units }) =>
            this.computeRentPSF({ prop, units: unitFilterFnc(units) }, minPsf),
          format: (value) => formatMoney(value, 2)
        },
        {
          label: {
            value: 'Rent/ft²',
            tooltip:
              'The average asking rent per square foot divides the sum of all rent by the sum of all square foot.'
          },
          name: 'avg_rent_psf',
          field: ({ prop, units }) =>
            this.computeRentPSF({ prop, units: unitFilterFnc(units) }, avgPsf),
          format: (value) => formatMoney(value, 2)
        },
        {
          label: {
            value: 'Maximum Rent/ft²',
            tooltip:
              'The maximum asking rent per square foot of all the units in the selected time period.'
          },
          name: 'max_rent_psf',
          field: ({ prop, units }) =>
            this.computeRentPSF({ prop, units: unitFilterFnc(units) }, maxPsf),
          format: (value) => formatMoney(value, 2)
        },
        {
          label: {
            value: 'Leases (Last 30-Days)',
            align: 'left',
            tooltip:
              this.daysInDateRange < 30
                ? 'The current date range filter is truncating this data. Adjust the date range to see the full value.'
                : 'The total number of units that have left the market (presumed leased) in the last 30 days.'
          },
          notApplicable: this.daysInDateRange < 30,
          name: 'leases_30_days',
          field: ({ prop, units }) =>
            this.computeLeasesOverNDays({ prop, units: unitFilterFnc(units) }, 30),
          format: (value) => (value ? formatNumber(value) : '')
        },
        {
          label: {
            value: 'Leases (Last 7-Days)',
            align: 'left',
            tooltip:
              this.daysInDateRange < 7
                ? 'The current date range filter is truncating this data. Adjust the date range to see the full value.'
                : 'The total number of units that have left the market (presumed leased) in the last 7 days.'
          },
          notApplicable: this.daysInDateRange < 7,
          name: 'leases_7_days',
          field: ({ prop, units }) =>
            this.computeLeasesOverNDays({ prop, units: unitFilterFnc(units) }, 7),
          format: (value) => (value ? formatNumber(value) : '')
        },
        {
          label: {
            value: 'Leases / Week (Last 4 Weeks)',
            align: 'left',
            tooltip:
              this.daysInDateRange < 28
                ? 'The current date range filter is truncating this data. Adjust the date range to see the full value.'
                : 'The average number of units leased per week over the past four full weeks.'
          },
          notApplicable: this.daysInDateRange < 28,
          name: 'leases_per_week',
          field: ({ prop, units }) =>
            this.computeAvgLeasesOverNWeeks({ prop, units: unitFilterFnc(units) }, 4),
          format: (value) => (value ? formatNumber(value, undefined, 1) : '')
        },
        {
          label: {
            value: 'Days Vacant',
            align: 'left',
            tooltip:
              'The average number of days the units have been vacant. If a unit is still on the market as vacant, we include the number of days since it first became vacant.'
          },
          name: 'days_vacant',
          field: ({ prop, units }) => this.computeDaysVacant({ prop, units: unitFilterFnc(units) }),
          format: (value) => formatNumber(value)
        },
        {
          label: {
            value: 'Days on Market',
            align: 'left',
            tooltip:
              'The number of days this unit has been on the market. If the unit is still on the market, we show the number of days since it was first listed.'
          },
          name: 'days_on_market',
          field: ({ prop, units }) => this.computeDaysOnMkt({ prop, units: unitFilterFnc(units) }),
          format: (value) => formatNumber(value)
        },
        {
          label: {
            value: 'Concession %',
            align: 'left',
            tooltip: 'Average monthly concession amount as a percent of asking rent.'
          },
          name: 'concession_percent',
          field: ({ prop, units }) =>
            this.computeConcessionPercent({ prop, units: unitFilterFnc(units) }),
          format: (value) => formatNumber(value, undefined, 1)
        },
        {
          label: {
            value: 'Concession Amount',
            align: 'left',
            tooltip: 'Average monthly concession amount over the lease term.'
          },
          name: 'concession_amount',
          field: ({ prop, units }) =>
            this.computeConcessionAmount({ prop, units: unitFilterFnc(units) }),
          format: (value) => formatMoney(value, 0)
        },
        {
          label: {
            value: 'Total Concession Amount',
            align: 'left',
            tooltip:
              'Total concession over the lease term, calculated by multiplying the monthly concession amount by the number of months in the lease term.'
          },
          name: 'total_concession_amount',
          field: ({ prop, units }) =>
            this.computeTotalConcessionAmount({ prop, units: unitFilterFnc(units) }),
          format: (value) => formatMoney(value, 0)
        },
        {
          label: {
            value: 'Cancel %',
            align: 'left',
            tooltip:
              'The percent of applications that resulted in a cancellation. If a unit was off the market for two or more days before returning, we consider it a cancellation.'
          },
          name: 'cancel_percent',
          field: ({ prop, units }) =>
            this.computeCancelPercent({ prop, units: unitFilterFnc(units) }),
          format: (value) => formatNumber(value, undefined, 1)
        },
        {
          label: {
            value: '# Cancels',
            align: 'left',
            tooltip:
              'The number of cancellations across the units. If a unit was off the market for two or more days before returning, we consider it a cancellation.'
          },
          name: 'num_cancels',
          field: ({ prop, units }) => this.computeNumCancels({ prop, units: unitFilterFnc(units) }),
          format: (value) => formatNumber(value)
        },
        {
          label: {
            value: '# Applications',
            align: 'left',
            tooltip: 'The total number of applications.'
          },
          name: 'num_applications',
          field: ({ prop, units }) => this.computeNumApps({ prop, units: unitFilterFnc(units) }),
          format: (value) => formatNumber(value)
        },
        {
          label: {
            value: '# Vacancies',
            align: 'left',
            tooltip: 'The total number of vacancies.'
          },
          name: 'num_vacancies',
          field: ({ prop, units }) =>
            this.computeNumVacancies({ prop, units: unitFilterFnc(units) }),
          format: (value) => formatNumber(value)
        },
        {
          label: {
            value: 'Asking Rent Trend',
            align: 'left',
            tooltip: 'Asking Rent PSF trend over time.'
          },
          name: 'asking_rent_trend',
          field: ({ prop, units }) =>
            this.computeAskingRentTrend({ prop, units: unitFilterFnc(units) })
        },
        {
          label: {
            value: 'Effective Rent Trend',
            align: 'left',
            tooltip: 'Effective Rent PSF trend over time.'
          },
          name: 'effective_rent_trend',
          field: ({ prop, units }) =>
            this.computeEffectiveRentTrend({ prop, units: unitFilterFnc(units) })
        }
      ] as GridRow[]
    },
    computeUnitTypeRows(bed: number) {
      const base = `${bed}_ut`
      const topBorder = `border-top: 2px solid ${this.$q.dark.isActive ? 'rgba(255, 255, 255, 0.28);' : 'rgba(0, 0, 0, 0.12);'}`
      const style = `border-left: 5px ${unitTypeColor(bed)} solid; text-align: right;`

      const rows = [
        // Unit type header
        {
          label: {
            value: bed === 0 ? 'Studio' : `${bed} Bed`,
            style: `${style} ${topBorder} font-weight: bold;`
          },
          name: `${base}_unit_title`,
          sortable: false,
          style: `${topBorder}`,
          field: () => ({ value: '', units: [] })
        },
        ...this.computeUnitDataRows(
          (units) => units.filter(({ unit }) => unit.bed === bed),
          bed
        ).map((row) => ({
          label: { ...row.label, style },
          name: `${base}_${row.name}`,
          field: row.field,
          format: row.format
        }))
      ]

      if (this.printingMode && !this.legacyPrintingMode) {
        // We include the header rows above each unit type for the PDF export
        const headerRows = [
          {
            label: { value: '', align: '' },
            name: `${base}_title`,
            sortable: false,
            style: `font-weight: bold; ${topBorder} page-break-before: always;`,
            field: this.computeTitle
          },
          {
            label: { value: '', align: '' },
            name: `${base}_street_view`,
            sortable: false,
            field: this.computeStreetView
          },
          {
            label: { value: '', align: '' },
            name: `${base}_building_name`,
            sortable: false,
            style: 'font-weight: bold;',
            field: this.computeBuildingName
          },
          {
            label: { value: '', align: '' },
            name: `${base}_street_address`,
            sortable: false,
            field: this.computeStreetAddress
          }
        ]
        rows.unshift(...headerRows)
      }
      return rows as GridRow[]
    },
    openUnitPopup(label: string, prop: PropertyDetailsResponse, field: FieldResult, row: GridRow) {
      const unitsWithProp = field.units.map((_) => {
        return {
          ..._,
          prop: prop
        }
      })
      this.$tracker.openedPopup(label, 'the_grid')
      this.displayUnitPopup = true
      this.clickedCell = { label, prop, field, unitsWithProp, row }
    },
    closeUnitPopup() {
      this.clickedCell = null
      this.displayUnitPopup = false
    },
    async resetVisibility() {
      if (this.userHasReadOnlyAccess) {
        return // Skip reset if user has read-only access
      }

      this.buildingSelectedRows = this.defaultBuildingSelect
      this.unitSelectedRows = DEFAULT_UNIT_SELECT
      await this.saveSelections()
    },
    openConcessionsPopup(prop: PropertyDetailsResponse) {
      this.$tracker.openedPopup('Concessions', 'the_grid')
      this.clickedConcessionsProperty = prop
      this.displayConcessionsPopup = true
    },
    closeConcessionsPopup() {
      this.clickedConcessionsProperty = null
      this.displayConcessionsPopup = false
    },
    labelClicked(name: string) {
      this.$tracker.changedSort('the_grid', name)
      if (name !== this.sortBy?.name) {
        this.sortBy = { name, direction: 'asc' }
      } else {
        if (this.sortBy?.direction === 'asc') {
          this.sortBy.direction = 'desc'
        } else if (this.sortBy?.direction === 'desc') {
          this.sortBy = null
        }
      }
    },
    async saveSelections() {
      const report = usePropertyReportStore().activeReport
      if (!isLoaded(report)) {
        return
      }

      if (this.userHasReadOnlyAccess) {
        return // Skip saving if user has read-only access
      }

      const view_preferences = report.data.view_preferences
        .filter((_) => _.name !== 'the_grid')
        .concat({
          name: 'the_grid',
          building_rows: this.buildingSelectedRows.map((_) => _.value),
          unit_rows: this.unitSelectedRows.map((_) => _.value)
        } as GridPreferences)
      await usePropertyReportStore().update({ ...report.data, view_preferences })
    },
    showUnitLabel(field: FieldResult): boolean {
      if (field.unit) {
        if (field.value === 0) {
          return !!field.showUnitOnZeroValue
        }
        return !!field.value
      }
      return false
    },
    pinSubjectColumn() {
      if (!this.pinSubject) {
        // the idea here is that if the subject column is pinned, we want the labels to be pinned too
        this.pinLabels = true
        this.pinSubject = true
      } else {
        this.pinAverage = false
        this.pinSubject = false
      }
    },
    pinLabelsColumn() {
      // if unpinning labels, make sure to unpin the subject / average column as well
      if (this.pinLabels) {
        if (this.pinSubject) {
          this.pinSubject = false
        }
        if (this.pinAverage) {
          this.pinAverage = false
        }
      }
      this.pinLabels = !this.pinLabels
    },
    pinAverageColumn() {
      if (!this.pinAverage) {
        // When pinning the average column, ensure labels are also pinned
        this.pinLabels = true
        this.pinAverage = true
        this.pinSubject = true
      } else {
        this.pinAverage = false
      }
    },
    computeAverageField(row: GridRow): FieldResult {
      if (this.sortedFilteredProperties.length === 0) {
        return { value: '', units: [] }
      }
      // Skip non-numeric rows
      const skipRows = [
        'title',
        'street_view',
        'building_name',
        'street_address',
        'management_company',
        'building_website',
        'concessions',
        'pricing_strategy',
        'effective_rent_trend',
        'asking_rent_trend'
      ]

      if (skipRows.includes(row.name)) {
        return { value: '', units: [] }
      }

      const values: number[] = []
      let sampleField: FieldResult | null = null

      for (const property of this.sortedFilteredProperties) {
        // Skip the subject property and average column in average calculations
        if (this.subjectDetail && property.prop.id === this.subjectDetail.id) {
          continue
        }
        if (property.prop.id === 'avg_column') {
          continue
        }
        const field = row.field(property)
        if (typeof field.value === 'number') {
          values.push(field.value)
          // Use the first valid field as sample to get the unit
          if (!sampleField) {
            sampleField = field
          }
        }
      }

      if (values.length === 0) {
        return { value: '', units: [] }
      }

      const average = avg(values)

      return {
        value: average ?? '',
        unit: sampleField?.unit,
        showUnitOnZeroValue: sampleField?.showUnitOnZeroValue,
        units: []
      }
    },
    getColumnClasses(prop: FilteredProp) {
      const classes: string[] = ['text-center', 'no-padding']

      // Subject column styling
      if (this.isSubjectColumn(prop)) {
        classes.push('subject', 'bg-deep-purple-light', 'text-primary')
      }

      // Average column styling
      if (this.isAverageColumn(prop)) {
        classes.push('average-column')
        if (this.$q.dark.isActive) {
          classes.push('bg-grey-9')
        } else {
          classes.push('bg-grey-1')
        }
      }

      // Sticky column logic
      if (this.shouldPinColumn(prop)) {
        classes.push('sticky-column')

        // Position classes for sticky columns
        if (this.isSubjectColumn(prop)) {
          classes.push('subject-sticky')
        } else if (this.isAverageColumn(prop)) {
          if (this.subjectDetail && this.subjectIsVisible) {
            classes.push('average-sticky')
          } else {
            classes.push('no-subject-sticky')
          }
        }
      }

      return classes.join(' ')
    },
    shouldPinColumn(prop: FilteredProp) {
      if (this.isSubjectColumn(prop) && this.pinSubject) {
        return true
      }
      return this.isAverageColumn(prop) && this.pinAverage
    },
    getColumnStyle(row: GridRow) {
      const baseStyle = row.style || ''

      return `max-width: 125px; white-space: wrap; ${baseStyle}`
    },
    exportCSV() {
      const data = []
      let index = 0
      const excludeRows = ['street_view', 'effective_rent_trend', 'asking_rent_trend']
      for (const row of this.visibleRows) {
        if (excludeRows.some((excludedRow) => row.name.includes(excludedRow))) {
          continue
        }
        data.push([])
        data[index].push(row.label.value)

        for (const property of this.sortedFilteredProperties) {
          if (property.prop.id === 'avg_column') {
            if (row.name === 'title') {
              data[index].push('Comp Avg')
            } else {
              const averageField = this.computeAverageField(row)
              const averageValue = row.format ? row.format(averageField.value) : averageField.value
              data[index].push(sanitizeFieldForCSV(averageValue))
            }
          } else {
            const field = row.field(property)
            let fieldValue = row.format ? row.format(field.value) : field.value
            if (row.name === 'pricing_strategy' && Array.isArray(fieldValue)) {
              fieldValue = fieldValue.join('\n')
            }
            data[index].push(sanitizeFieldForCSV(fieldValue))
          }
        }

        index += 1
      }
      const today = new Date().toISOString().split('T')[0]
      exportFile(`hello-data-rent-comps-overview-${today}.csv`, data.join('\n'))
      this.$tracker.exported('overview_grid')
    },
    isSubjectColumn(prop: FilteredProp) {
      return this.subjectDetail && prop.prop.id === this.subjectDetail.id
    },
    isAverageColumn(prop: FilteredProp) {
      return prop.prop.id === 'avg_column'
    }
  }
}
</script>
<style scoped lang="scss">
.sticky-column {
  position: sticky;
  background-color: white;
  z-index: 1;

  &.label {
    left: 0;
    min-width: 215px;
    width: 215px;
  }

  &.subject-sticky {
    left: 215px; /* After labels */
  }

  &.average-sticky {
    left: 340px; /* After labels + subject (215px + 125px) */
  }

  &.no-subject-sticky {
    left: 215px; /* After labels when no subject visible */
  }
}
.body--dark {
  .sticky-column {
    background-color: $dark;
  }

  .sticky-row {
    background-color: $dark;
  }
}

/* Note: positioning is set for each row in the setStickyRowOffsets function */
.sticky-row {
  position: sticky;
  background-color: white;
  top: 0;
  z-index: 2;
}
.sticky-row:nth-child(4) {
  top: 55px;
  box-shadow: 0px 1px 1px -1px black;
}
@media print {
  .sticky-row:nth-child(4) {
    box-shadow: none;
  }
}
tr {
  #sort-icon-swap-vert {
    display: none;
  }
  :hover {
    #sort-icon-swap-vert {
      display: inline-block;
    }
  }
  .push-pin-icon {
    display: none;
  }
  :hover {
    .push-pin-icon {
      display: inline-block;
    }
  }
}

.average-column {
  min-width: 80px;
  max-width: 80px;
  width: 80px;
  white-space: wrap;
  font-size: 11px;
  line-height: 11px;
}
</style>
